import React, { lazy, Suspense, useState, useEffect, useCallback } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';

import { CookieConsentBanner } from '@/components/CookieConsentBanner';
import { WorkoutHomeLayout } from '@/features/workout-generator/components/home';
import { AuthScreen } from './src/components/AuthScreen';
import PricingModal from './src/components/PricingModal';
import { GuestLimitModal } from './src/components/GuestLimitModal';

const LandingPage = lazy(() => import('./src/components/LandingPage'));
const Success = lazy(() => import('./src/components/Success'));
const Cancel = lazy(() => import('./src/components/Cancel'));
const TermsOfService = lazy(() => import('./src/components/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./src/components/PrivacyPolicy'));
const SettingsPage = lazy(() => import('./src/components/SettingsPage'));
import { Toaster, toast } from 'react-hot-toast';

import { TRANSLATIONS, createStringT } from './translations';
import { SportType, RunIntensity } from './types';
import {
  WorkoutHistoryItem,
  inferSportFromCachedData,
  formatHistoryEditDate,
  useWorkoutHistory,
} from '@/features/workout-history';

import { useAuth } from './src/hooks/useAuth';
import { useUserSettings } from './src/hooks/useUserSettings';
import { useWorkoutSettings } from '@/features/workout-generator/hooks/useWorkoutSettings';
import { useWorkoutGenerator } from '@/features/workout-generator/hooks/useWorkoutGenerator';
import { parseCacheKeyToSportMeta, savePersistedWorkoutSession } from '@/features/workout-generator/services/workoutSessionPersistence';
import { useWorkoutExport } from '@/features/export-hub';
import { useStoredFtp } from './src/hooks/useStoredFtp';
import { useFitFtpGate } from '@/features/fit-ftp/hooks/useFitFtpGate';
import { FitFtpModal } from '@/components/FitFtpModal';
import { useMobileLayout } from '@/shared/hooks/useMobileLayout';
import { isP2tOnboardingDone, useGuestLandingFlow } from '@/features/landing/hooks/useGuestLandingFlow';
import { usePromptRegenerationHints } from '@/features/workout-generator/hooks/usePromptRegenerationHints';
import type { PromptInspirationItem } from '@/features/explore/types';
import { AI_PROCESSING_CONSENT_STORAGE_KEY } from '@/shared/legal/consentStorage';
import { supabase } from '@/core/supabaseClient';

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, user, authLoading, email, setEmail, password, setPassword, isSigningUp, setIsSigningUp, handleSignUp, handleLogin, handleLogout, isPro } = useAuth();
  console.log('App State Check:', { authLoading, session: !!session, user: !!user });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showGuestLimitModal, setShowGuestLimitModal] = useState(false);
  const { language, setLanguage, theme, setTheme } = useUserSettings();
  const {
    guestGenerations,
    setGuestGenerations,
    setShowLandingPage,
    handleTryForFreeClick: persistOnboardingComplete,
  } = useGuestLandingFlow();

  const handleTryForFreeClick = useCallback(() => {
    persistOnboardingComplete();
    navigate('/generator', { replace: true });
  }, [navigate, persistOnboardingComplete]);
  const [aiProcessingConsent, setAiProcessingConsent] = useState(() => {
    try {
      return localStorage.getItem(AI_PROCESSING_CONSENT_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [aiConsentLocked] = useState(() => {
    try {
      return localStorage.getItem(AI_PROCESSING_CONSENT_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [freeTierRemaining, setFreeTierRemaining] = useState<number | null>(null);
  const [freeTierLimit, setFreeTierLimit] = useState<number>(3);

  const onAiProcessingConsentChange = useCallback((accepted: boolean) => {
    setAiProcessingConsent(accepted);
    try {
      if (accepted) {
        localStorage.setItem(AI_PROCESSING_CONSENT_STORAGE_KEY, 'true');
      } else {
        localStorage.removeItem(AI_PROCESSING_CONSENT_STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
  }, []);
  const { ftp: storedFtp, setFtp: persistUserFtp } = useStoredFtp(user?.id ?? null);
  const {
    fitFtpModalOpen,
    ensureFtpForCyclingFit,
    cancelFitFtpModal,
    confirmFitFtpModal,
  } = useFitFtpGate(storedFtp);
  const {
    sport, setSport,
    runIntensity, setRunIntensity,
    bikeIntensity, setBikeIntensity,
    activeIntensity,
    prompts, setPrompts,
    activeZones,
    updateZoneBound,
    unitLabelKey,
    activeMethodLabel,
    isCustomActive,
    validation: zoneValidation,
    saveActiveZones,
    saving: savingZones,
    canSave: canSaveZones,
    currentPrompt,
  } = useWorkoutSettings(user);

  const handleSaveZones = useCallback(async () => {
    const result = await saveActiveZones();
    if (result.ok) {
      toast.success(TRANSLATIONS[language].zonesSavedToast);
    } else if (result.error && result.error !== 'no-user' && result.error !== 'invalid') {
      toast.error(TRANSLATIONS[language].zonesSaveError.replace('{message}', result.error));
    }
  }, [saveActiveZones, language]);

  const [historyLoadedDateLabel, setHistoryLoadedDateLabel] = useState<string | null>(null);
  const [historyLoadedForSport, setHistoryLoadedForSport] = useState<SportType | null>(null);
  const [historyLoadedWorkoutName, setHistoryLoadedWorkoutName] = useState<string | null>(null);
  const [homeSection, setHomeSection] = useState<'builder' | 'explore'>('builder');

  const clearHistoryEditBanner = useCallback(() => {
    setHistoryLoadedDateLabel(null);
    setHistoryLoadedForSport(null);
    setHistoryLoadedWorkoutName(null);
  }, []);

  const restorePersistedWorkoutSession = useCallback(
    (meta: { cacheKey: string; sourcePrompt: string }) => {
      const { sport: restoredSport, runIntensity: restoredRun } = parseCacheKeyToSportMeta(meta.cacheKey);
      setSport(restoredSport);
      if (restoredSport === 'run' && restoredRun) {
        setRunIntensity(restoredRun);
      }
      if (meta.sourcePrompt) {
        setPrompts((p) => ({ ...p, [restoredSport]: meta.sourcePrompt }));
      }
    },
    [setSport, setRunIntensity, setPrompts],
  );

  const { fetchWorkouts, workouts, fetchingWorkouts, handleDeleteWorkout, deletingWorkoutId } = useWorkoutHistory(user, session);

  const { isLoading, generationPhaseLabel, resultsCache, setResultsCache, activePlatform, setActivePlatform, segments, aiWorkout, generatedXml, generatedMarkdown, workoutName, currentWorkoutId, handleGenerate } = useWorkoutGenerator(
    session, user, currentPrompt, activeZones, sport, runIntensity, language, fetchWorkouts, guestGenerations, setGuestGenerations, setShowGuestLimitModal, isPro, setShowPricingModal, setShowLandingPage,
    clearHistoryEditBanner,
    activeIntensity,
    restorePersistedWorkoutSession,
    zoneValidation.isValid
  );

  const {
    promptTextareaRef,
    placeholderHintIndex,
    regenerateHintPulse,
    setRegenerateHintPulse,
    handleChipSelect,
  } = usePromptRegenerationHints(segments.length, sport, setPrompts);

  const handleUseInspirationTemplate = useCallback(
    (item: PromptInspirationItem) => {
      setSport(item.sport);
      setPrompts((p) => ({ ...p, [item.sport]: item.prompt_text }));
      clearHistoryEditBanner();
      setRegenerateHintPulse(false);
      setHomeSection('builder');
      requestAnimationFrame(() => {
        const el = promptTextareaRef.current;
        if (!el) return;
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setSport, setPrompts, clearHistoryEditBanner, setRegenerateHintPulse, promptTextareaRef],
  );

  const onGenerateClick = useCallback(() => {
    if (!aiProcessingConsent) {
      toast.error(TRANSLATIONS[language].aiProcessingConsentRequiredToast);
      return;
    }
    setRegenerateHintPulse(false);
    void handleGenerate();
  }, [aiProcessingConsent, handleGenerate, language, setRegenerateHintPulse]);

  const refreshFreeTierStatus = useCallback(async () => {
    if (!session) {
      setFreeTierRemaining(null);
      return;
    }
    try {
      const {
        data: { session: authSession },
      } = await supabase.auth.getSession();
      const bearer = authSession?.access_token;
      if (!bearer) return;
      const res = await fetch('/.netlify/functions/free-tier-status', {
        method: 'GET',
        headers: { Authorization: `Bearer ${bearer}` },
      });
      const payload = (await res.json().catch(() => ({}))) as {
        isPro?: boolean;
        freeTierRemaining?: number | null;
        freeTierLimit?: number;
      };
      if (!res.ok || payload.isPro) {
        setFreeTierRemaining(null);
        return;
      }
      setFreeTierRemaining(typeof payload.freeTierRemaining === 'number' ? payload.freeTierRemaining : 0);
      if (typeof payload.freeTierLimit === 'number') setFreeTierLimit(payload.freeTierLimit);
    } catch {
      /* ignore */
    }
  }, [session]);

  useEffect(() => {
    void refreshFreeTierStatus();
  }, [refreshFreeTierStatus, workouts.length]);

  const handleLoadFromHistory = useCallback(
    (workout: WorkoutHistoryItem) => {
      const data = workout.data_json;
      if (!data?.segments?.length) {
        toast.error(TRANSLATIONS[language].historyLoadError);
        return;
      }

      const loadedSport = inferSportFromCachedData(data);
      const loadedRunIntensity: RunIntensity =
        loadedSport === 'run' ? data.runIntensity ?? 'pace' : 'pace';

      setSport(loadedSport);
      if (loadedSport === 'run') {
        setRunIntensity(loadedRunIntensity);
      }

      const cacheKey = loadedSport === 'bike' ? 'bike' : `run-${loadedRunIntensity}`;
      const promptText = workout.prompt ?? '';

      setResultsCache((prev) => {
        const next = {
          ...prev,
          [cacheKey]: {
            workoutId: workout.id,
            xml: data.xml,
            markdown: data.markdown,
            segments: data.segments,
            aiWorkout: data.aiWorkout,
            workoutName: data.workoutName || workout.name,
            sourcePrompt: data.sourcePrompt ?? promptText,
            sport: loadedSport,
            runIntensity: loadedSport === 'run' ? loadedRunIntensity : undefined,
          },
        };
        savePersistedWorkoutSession(cacheKey, next);
        return next;
      });

      setPrompts((p) => ({
        ...p,
        [loadedSport]: promptText,
      }));

      setHistoryLoadedDateLabel(formatHistoryEditDate(workout.created_at, language));
      setHistoryLoadedForSport(loadedSport);
      setHistoryLoadedWorkoutName(data.workoutName || workout.name || null);

      setActivePlatform('tp');

      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [language, setSport, setRunIntensity, setResultsCache, setPrompts, setActivePlatform]
  );

  const {
    copied,
    tpSending,
    garminSending,
    sendToTrainingPeaks,
    sendToGarminConnect,
    handleDownloadZwo,
    handleDownloadFit: downloadFitWithResolvedFtp,
    handleShareWorkoutDescription,
    copyToClipboard,
    sending,
    sendToCalendar,
  } = useWorkoutExport(
    generatedXml,
    generatedMarkdown,
    workoutName,
    language,
    isPro,
    setShowPricingModal,
    segments,
    sport,
    storedFtp,
    activeZones,
    aiWorkout,
  );

  const handleDownloadFit = useCallback(async () => {
    const ftp = await ensureFtpForCyclingFit();
    if (ftp == null) return;
    await downloadFitWithResolvedFtp(ftp);
  }, [ensureFtpForCyclingFit, downloadFitWithResolvedFtp]);

  const isMobileLayout = useMobileLayout();
  const [canShareText, setCanShareText] = useState(false);
  useEffect(() => {
    setCanShareText(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  const t = TRANSLATIONS[language];
  const tStr = createStringT(language);

  useEffect(() => {
    const homePl = {
      title: 'Prompt2Train | Treningi AI dla kolarzy, biegaczy i triathlonistów',
      description:
        'Wygeneruj ustrukturyzowany plan treningowy dla kolarstwa, biegania i triathlonu za pomocą AI. Plany dla triathlonistów i treningi triathlonowe z eksportem do Garmin Connect, TrainingPeaks i Polar Flow.',
    };
    const homeEn = {
      title: 'Prompt2Train | Turn your description into workouts for Garmin, Polar & TrainingPeaks',
      description:
        'The fastest path to a structured workout: describe your session and AI prepares a ready plan with direct export to your watch or calendar.',
    };
    const home = language === 'pl' ? homePl : homeEn;
    const byPath: Record<string, { title: string; description: string }> = {
      '/': home,
      '/generator': home,
      '/success': {
        title: 'Prompt2Train PRO aktywowany',
        description:
          'Twój plan Prompt2Train PRO jest aktywny. Generuj nielimitowane treningi AI dla kolarstwa, biegania i triathlonu.',
      },
      '/cancel': {
        title: 'Płatność Prompt2Train anulowana',
        description:
          'Płatność została anulowana. Możesz wrócić i ulepszyć konto do Prompt2Train PRO w dowolnej chwili.',
      },
      '/privacy-policy': {
        title: 'Polityka prywatności — Prompt2Train',
        description:
          'Polityka prywatności kreatora treningów AI Prompt2Train dla kolarzy, biegaczy i triathlonistów, w tym integracji z Garmin Connect i Polar Flow.',
      },
      '/terms-of-service': {
        title: 'Regulamin — Prompt2Train',
        description:
          'Regulamin korzystania z Prompt2Train — inteligentnego generatora ustrukturyzowanych treningów kolarskich, biegowych i triathlonowych.',
      },
    };
    const fallback = byPath['/'];
    const active = byPath[location.pathname] ?? fallback;
    document.title = active.title;
    const ensureMeta = (attr: 'name' | 'property', key: string, value: string) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };
    const ensureCanonical = (href: string) => {
      let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };
    const fullUrl = `https://prompt2train.fit${location.pathname}`;
    ensureMeta('name', 'description', active.description);
    ensureMeta('property', 'og:title', active.title);
    ensureMeta('property', 'og:description', active.description);
    ensureMeta('property', 'og:url', fullUrl);
    ensureMeta('property', 'og:site_name', 'Prompt2Train');
    ensureMeta('property', 'og:type', 'website');
    ensureMeta('name', 'twitter:card', 'summary_large_image');
    ensureMeta('name', 'twitter:title', active.title);
    ensureMeta('name', 'twitter:description', active.description);
    ensureCanonical(fullUrl);
  }, [location.pathname, language]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-200">
        Ładowanie...
      </div>
    );
  }

  const routeFallback = (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-200">
      Ładowanie...
    </div>
  );

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <Toaster position="top-right" />
      <FitFtpModal
        open={fitFtpModalOpen}
        language={language}
        onCancel={cancelFitFtpModal}
        onConfirm={(n, save) => {
          if (save) void persistUserFtp(n);
          confirmFitFtpModal(n);
        }}
      />
      <CookieConsentBanner language={language} t={t} />
      <Suspense fallback={routeFallback}>
        <Routes>
          <Route path="/success" element={<Success />} />
          <Route path="/cancel" element={<Cancel />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/settings"
            element={
              !isP2tOnboardingDone() ? (
                <Navigate to="/" replace />
              ) : !user ? (
                <Navigate to="/generator" replace />
              ) : (
                <SettingsPage userId={user.id} userEmail={user.email ?? undefined} language={language} isPro={isPro} />
              )
            }
          />
          <Route
            path="/generator"
            element={
              !isP2tOnboardingDone() ? (
                <Navigate to="/" replace />
              ) : (
                <div className="min-h-[100svh] min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 text-slate-800 dark:bg-gradient-to-br dark:from-gray-900 dark:to-slate-950 dark:text-slate-200 p-4 md:p-6 transition-colors duration-300">
                  <WorkoutHomeLayout
                    isPro={isPro}
                    guestGenerations={guestGenerations}
                    exploreNav={{
                      section: homeSection,
                      setSection: setHomeSection,
                      onUseInspirationTemplate: handleUseInspirationTemplate,
                    }}
                    header={{
                      isPro,
                      t,
                      session,
                      user,
                      theme,
                      language,
                      setLanguage,
                      setTheme,
                      setShowPricingModal,
                      setShowAuthModal,
                      handleLogout,
                    }}
                    sportTabs={{ sport, setSport, t }}
                    prompt={{
                      t,
                      tStr,
                      language,
                      sport,
                      activeIntensity,
                      setBikeIntensity,
                      setRunIntensity,
                      activeZones,
                      updateZoneBound,
                      unitLabelKey,
                      methodLabel: activeMethodLabel,
                      isCustomZones: isCustomActive,
                      zoneValidation,
                      saveActiveZones: handleSaveZones,
                      canSaveZones,
                      savingZones,
                      historyLoadedDateLabel,
                      historyLoadedForSport,
                      historyLoadedWorkoutName,
                      promptTextareaRef,
                      currentPrompt,
                      setPrompts,
                      placeholderHintIndex,
                      handleChipSelect,
                      segmentsLength: segments.length,
                      onGenerateClick,
                      isLoading,
                      generationPhaseLabel,
                      regenerateHintPulse,
                      aiProcessingConsent,
                      onAiProcessingConsentChange,
                      freeTierRemaining,
                      freeTierLimit,
                      showUpgradeHint: !isPro,
                      onUpgradeClick: () => setShowPricingModal(true),
                      aiConsentCheckboxHidden: aiConsentLocked,
                    }}
                    visualizer={{
                      t,
                      theme,
                      language,
                      sport,
                      runIntensity,
                      activeZones,
                      segments,
                      aiWorkout,
                      workoutName,
                      isLoading,
                    }}
                    exportPanel={{
                      t,
                      sport,
                      segments,
                      isLoading,
                      activePlatform,
                      setActivePlatform,
                      generatedMarkdown,
                      copied,
                      tpSending,
                      garminSending,
                      isMobileLayout,
                      canShareText,
                      sendToTrainingPeaks,
                      sendToGarminConnect,
                      sendToPolarFlow: sendToCalendar,
                      handleDownloadZwo,
                      handleDownloadFit,
                      handleShareWorkoutDescription,
                      copyToClipboard,
                      cloudSending: sending,
                    }}
                    history={{
                      t,
                      language,
                      session,
                      fetchingWorkouts,
                      workouts,
                      onSelectWorkout: handleLoadFromHistory,
                      onDeleteWorkout: handleDeleteWorkout,
                      deletingWorkoutId,
                      onOpenLogin: () => setShowAuthModal(true),
                    }}
                  />

                  <AuthScreen
                    showAuth={showAuthModal}
                    setShowAuth={setShowAuthModal}
                    email={email}
                    setEmail={setEmail}
                    password={password}
                    setPassword={setPassword}
                    isSigningUp={isSigningUp}
                    setIsSigningUp={setIsSigningUp}
                    handleSignUp={handleSignUp}
                    handleLogin={handleLogin}
                    theme={theme}
                    language={language}
                  />

                  <GuestLimitModal
                    showModal={showGuestLimitModal}
                    setShowModal={setShowGuestLimitModal}
                    language={language}
                    onLoginSignUp={() => {
                      setShowGuestLimitModal(false);
                      setShowAuthModal(true);
                    }}
                  />

                  <PricingModal
                    isOpen={showPricingModal}
                    onClose={() => setShowPricingModal(false)}
                    language={language}
                    onRequestLogin={() => {
                      setShowPricingModal(false);
                      setShowAuthModal(true);
                    }}
                  />
                </div>
              )
            }
          />
          <Route
            path="/"
            element={
              isP2tOnboardingDone() ? (
                <Navigate to={{ pathname: '/generator', search: location.search }} replace />
              ) : (
                <LandingPage onTryForFreeClick={handleTryForFreeClick} t={t} />
              )
            }
          />
        </Routes>
      </Suspense>
    </div>
  );
}
